import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal } from 'reactstrap';
import { convertCurrency, formatModifyDate } from '../Common/CommonLogic';
import { cloneDeep } from 'lodash';
import { CurrencyWiseTotal } from '../../pages/InstantRate/Preview/PreviewCommonTable';
import { chargesPriority } from "../../pages/Dashboard/Partials/initialData";
import { cube_filled, oocl_logo, zim_logo } from '../../assets/images';


const PriceBreakDownModal = ({ quotationData, quotationId, isPriceBreakDownModalOpen, priceBreakDownModalHandler, inquiriesType }) => {
    const quoteData = useSelector((state) => state.instantRate.quote_selected_data);
    const quoteSelectedTenantdata = useSelector((state) => state.instantRate.quote_selected_tenantdata);
    const { currency_ExchangeRate } = useSelector((state) => state.quotation);
    const { smartQuotationsValues } = useSelector((state) => state.instantRate);
    const { viewBidsBreakdownError } = useSelector((state) => state.sales);
    const [currencyCode, setCurrencyCode] = useState('');
    const [processedQuoteData, setProcessedQuoteData] = useState(null);
    const [carrierMaster, setCarrierMaster] = useState(null);
    const [remarks, setRemarks] = useState('');

    // Process quote data when it changes or when currency changes
    useEffect(() => {
        // Make sure quoteData exists and has the expected structure
        //console.log(quoteData, "quoteData");
        console.log(quoteSelectedTenantdata?.tenantFclQuotationCarrierDetails?.[0]?.carrierMaster, "quoteSelectedTenantdata");
        if (quoteSelectedTenantdata) {
            setCarrierMaster(quoteSelectedTenantdata?.tenantFclQuotationCarrierDetails?.[0]?.carrierMaster);
            setRemarks(quoteSelectedTenantdata?.tenantFclQuotationCarrierDetails?.[0]?.remarks || '');
        }
        if (viewBidsBreakdownError) {
            setProcessedQuoteData(null);
            return;
        }
        if (!quoteData || !quoteData[0] || !Array.isArray(quoteData[0]?.tariffDetails)) {
            console.log('Quote data is not in the expected format');
            setProcessedQuoteData(null);
            return;
        }

        try {
            // Create a deep copy to avoid mutating the original data
            const clonedData = cloneDeep(quoteData[0]);

            console.log('Cloned Quote Data:', clonedData);

            // Process each tariff detail
            clonedData.tariffDetails.forEach((item) => {
                // Skip if fclTariffBreakDowns is not an array
                if (!Array.isArray(item?.fclTariffBreakDowns)) return;

                // Process each charge
                item.fclTariffBreakDowns.forEach((charge) => {
                    if (charge?.currencyCode && smartQuotationsValues?.quote_currency?.currencyCode) {
                        // Find currency conversion rate
                        const filterCurrency = currency_ExchangeRate?.find(data =>
                            data?.baseCurrency?.currencyCode === charge.currencyCode &&
                            data?.targetCurrency?.currencyCode === smartQuotationsValues.quote_currency.currencyCode
                        );

                        if (filterCurrency) {
                            // Add conversion info to the cloned object
                            charge.convertedAmount = charge.amount * filterCurrency.value;
                            charge.conversionRate = filterCurrency.value;
                        }
                    }
                });
            });

            // Update state with the processed data
            setProcessedQuoteData(clonedData);
        } catch (error) {
            console.error('Error processing quote data:', error);
            setProcessedQuoteData(null);
        }
    }, [quoteData, quoteSelectedTenantdata, smartQuotationsValues?.quote_currency, currency_ExchangeRate, viewBidsBreakdownError, inquiriesType]);

    // Calculate total for a section of charges
    const innerTotalHandler = (inquiriesType, array, newArray) => {
        // Check if we have a valid currency selected
        console.log(currencyCode, smartQuotationsValues?.quote_currency?.currencyCode, inquiriesType, "currencyCode");
        if (currencyCode && smartQuotationsValues?.quote_currency?.currencyCode && inquiriesType === 'OCEAN_FCL') {
            // Use converted amounts when available
            let amt = array?.reduce((total, charge) => {
                // Use convertedAmount if available, otherwise fallback to original amount
                return total + (charge?.convertedAmount || charge?.amount || 0);
            }, 0);

            // Add amounts from newArray if provided
            if (newArray !== undefined && Array.isArray(newArray)) {
                amt += newArray.reduce((total, charge) => {
                    return total + (charge?.convertedAmount || Number(charge?.total_sale_cost) || 0);
                }, 0);
            }

            return Math.ceil(amt);
        } else {
            // No currency conversion, just sum the original amounts
            let amt = array?.reduce((total, charge) =>
                total + (charge?.amount || 0), 0);

            // Add amounts from newArray if provided
            if (newArray !== undefined && Array.isArray(newArray)) {
                amt += newArray.reduce((total, charge) =>
                    total + (Number(charge?.total_sale_cost) || 0), 0);
            }

            return Math.ceil(amt);
        }
    }

    // Calculate origin total from all charges
    const calculateOriginTotal = () => {
        if (!processedQuoteData?.tariffDetails) return 0;

        let originTotal = 0;
        processedQuoteData.tariffDetails.forEach(item => {
            if (!item?.fclTariffBreakDowns) return;

            item.fclTariffBreakDowns.forEach(charge => {
                // Only count charges at origin
                if (charge.chargeAt === 'ORIGIN') {
                    originTotal += currencyCode && charge.convertedAmount
                        ? charge.convertedAmount
                        : (charge.amount || 0);
                }
            });
        });

        return Math.ceil(originTotal);
    }

    // Calculate destination total from all charges
    const calculateDestinationTotal = () => {
        if (!processedQuoteData?.tariffDetails) return 0;

        let destinationTotal = 0;
        processedQuoteData.tariffDetails.forEach(item => {
            if (!item?.fclTariffBreakDowns) return;

            item.fclTariffBreakDowns.forEach(charge => {
                // Only count charges at destination
                if (charge.chargeAt === 'DESTINATION') {
                    destinationTotal += currencyCode && charge.convertedAmount
                        ? charge.convertedAmount
                        : (charge.amount || 0);
                }
            });
        });

        return Math.ceil(destinationTotal);
    }

    // Custom modal style to increase width only
    const modalStyle = {
        maxWidth: '80%',
        width: '100%',
    };


    // Update currency code when quote_currency changes
    useEffect(() => {
        if (smartQuotationsValues?.quote_currency &&
            typeof smartQuotationsValues.quote_currency === 'object' &&
            Object.keys(smartQuotationsValues.quote_currency).length > 0) {
            setCurrencyCode(smartQuotationsValues.quote_currency.currencyCode);
        } else {
            setCurrencyCode('');
        }
    }, [smartQuotationsValues?.quote_currency]);

    return (
        <>
            <Modal
                size="xl"
                isOpen={isPriceBreakDownModalOpen}
                toggle={() => { priceBreakDownModalHandler(); }}
                style={modalStyle}
            >
                <div className="p-4 bg-white border rounded shadow">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4 className="fw-bold m-0">Price Breakup</h4>
                        <h4 className="fw-bold m-0">Quotation Id : {quotationId}</h4>
                        <button
                            type="button"
                            className="btn-close fs-5"
                            onClick={() => priceBreakDownModalHandler()}
                            aria-label="Close"
                        ></button>
                    </div>
                    <hr />
                    {carrierMaster !== null && <div className="d-inline-flex align-items-center px-3 py-2 rounded bg-light mb-3" style={{ backgroundColor: '#f0f4ff' }}>
                        <img
                            src={carrierMaster?.logo ? carrierMaster.logo : cube_filled}
                            alt="Logo"
                            style={{ height: '28px', width: '28px', objectFit: 'contain', marginRight: '10px' }}
                            onError={e => {
                                e.target.src = carrierMaster?.carrierCode?.toLowerCase() === 'oocl'
                                    ? oocl_logo
                                    : carrierMaster?.carrierCode?.toLowerCase() === 'zim'
                                        ? zim_logo
                                        : cube_filled;
                            }}
                        />
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#0d47a1' }}>
                            {carrierMaster?.name || '-'}{' '}
                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#0d6efd', marginLeft: '4px' }}>
                                ({carrierMaster?.carrierCode || '-'})
                            </span>
                        </span>
                    </div>}
                    

                    {processedQuoteData && (
                        <div className={`d-flex flex-wrap ${processedQuoteData?.oceanTransitTime !== 0 && processedQuoteData?.validTo !== "" ? "justify-content-between" : "justify-content-center"} mb-3 text-muted fs-5`}>
                            {processedQuoteData?.oceanTransitTime !== 0 && <div><strong>ETT:</strong> {processedQuoteData?.oceanTransitTime || '-'} Days</div>}
                            <div><strong>Route:</strong> {processedQuoteData?.originName || quotationData?.origin || '-'} &gt; {processedQuoteData?.destinationName || quotationData?.destination || '-'}</div>
                            {processedQuoteData?.validTo && <div><strong>Valid till:</strong> {processedQuoteData?.validTo ? formatModifyDate(processedQuoteData?.validTo) : '-'}</div>}
                        </div>
                    )}

                    {inquiriesType === 'AIR' &&
                        [...(processedQuoteData?.tariffDetails || [])]
                            .sort((a, b) => {
                                const aPriority = chargesPriority[a.header] ?? 99;
                                const bPriority = chargesPriority[b.header] ?? 99;
                                return aPriority - bPriority;
                            })
                            .map((item, index) => (
                                <div className="mb-4" key={index}>
                                    <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-secondary bg-opacity-25">
                                        <div className="fw-semibold fs-5">{item?.header?.split('_').join(" ") || '-'}</div>
                                        {inquiriesType === 'AIR' && (
                                            <div className="fw-semibold fs-5">{innerTotalHandler(inquiriesType, (item?.fclTariffBreakDowns || []), [])}</div>
                                        )}
                                        
                                        {inquiriesType === 'OCEAN_FCL' && (
                                            <div className="fw-semibold fs-5">{`${currencyCode !== '' ? `${currencyCode} ` : ''}`} {currencyCode !== '' ? innerTotalHandler(inquiriesType, (item?.fclTariffBreakDowns || []), []) : ''}</div>
                                        )}
                                    </div>
                                    <div className="border">
                                        <div className="row g-0 fw-semibold text-muted fs-6 px-3 py-2 border-bottom bg-light">
                                            <div className="row g-0 fw-semibold text-muted fs-6 px-3 py-2 border-bottom bg-light">
                                                <div className="col-4">Description</div>
                                                <div className="col">Base</div>
                                                <div className="col text-center">Unit(s)</div>
                                                {/* <div className="col-1">Currency</div> */}
                                                <div className="col text-end">Total Amount</div>
                                                {/* <div className="col text-center">ROE</div> */}
                                                {/* <div className="col text-end">Amount {`${currencyCode !== '' ? `in (${currencyCode})` : ''}`}</div> */}
                                            </div>
                                        </div>
                                        {item?.fclTariffBreakDowns.map((charge, chargeIndex) => (
                                            <div key={chargeIndex} className="row g-0 fs-6 px-3 py-2 border-top">
                                                <div className="col-4">{charge.componentDescription}</div>
                                                <div className="col">{charge.uomDescription}</div>
                                                <div className="col text-center">{charge.unit}</div>
                                                {/* <div className="col-1">{charge.currencyCode}</div> */}
                                                <div className="col text-end">{(charge.amount || charge.total_sale_cost || 0).toFixed(2)}</div>
                                                {/* <div className="col text-center">{charge.conversionRate ? charge.conversionRate : '1'}</div> */}
                                                {/* <div className="col text-end">
                                                    {charge.convertedAmount
                                                        ? charge.convertedAmount.toFixed(2)
                                                        : (charge.amount || charge.total_sale_cost || 0).toFixed(2)}
                                                </div> */}
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            ))
                    }

                    {/* Ocean Charges */}
                    {inquiriesType === 'OCEAN_FCL' &&
                        [...(processedQuoteData?.tariffDetails || [])]
                            .sort((a, b) => {
                                const aPriority = chargesPriority[a.header] ?? 99;
                                const bPriority = chargesPriority[b.header] ?? 99;
                                return aPriority - bPriority;
                            })
                            .map((item, index) => (
                                <div className="mb-4" key={index}>
                                    <div className="d-flex justify-content-between align-items-center py-2 px-3 bg-secondary bg-opacity-25">
                                        <div className="fw-semibold fs-5">{item?.header?.split('_').join(" ") || '-'}</div>
                                
                                        <div className="fw-semibold fs-5">{`${currencyCode !== '' ? `${currencyCode} ` : ''}`} {currencyCode !== '' ? innerTotalHandler(inquiriesType, (item?.fclTariffBreakDowns || []), []) : ''}</div>
                                    </div>
                                    <div className="border">
                                        <div className="row g-0 fw-semibold text-muted fs-6 px-3 py-2 border-bottom bg-light">
                                            <div className="row g-0 fw-semibold text-muted fs-6 px-3 py-2 border-bottom bg-light">
                                                <div className="col-4">Description</div>
                                                <div className="col">Base</div>
                                                <div className="col text-center">Unit(s)</div>
                                                <div className="col-1">Currency</div>
                                                <div className="col text-end">Total Amount</div>
                                                <div className="col text-center">ROE</div>
                                                <div className="col text-end">Amount {`${currencyCode !== '' ? `in (${currencyCode})` : ''}`}</div>
                                            </div>
                                        </div>
                                        {item?.fclTariffBreakDowns.map((charge, chargeIndex) => (
                                            <div key={chargeIndex} className="row g-0 fs-6 px-3 py-2 border-top">
                                                <div className="col-4">{charge.componentDescription}</div>
                                                <div className="col">{charge.uomDescription}</div>
                                                <div className="col text-center">{charge.unit}</div>
                                                <div className="col-1">{charge.currencyCode}</div>
                                                <div className="col text-end">{(charge.amount || charge.total_sale_cost || 0).toFixed(2)}</div>
                                                <div className="col text-center">{charge.conversionRate ? charge.conversionRate : '1'}</div>
                                                <div className="col text-end">
                                                    {charge.convertedAmount
                                                        ? charge.convertedAmount.toFixed(2)
                                                        : (charge.amount || charge.total_sale_cost || 0).toFixed(2)}
                                                </div>
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            ))
                    }

                    {/* Footer - Total Table */}
                    <div className="mt-4 border w-50">
                        <table className="table table-bordered mb-0">
                            <CurrencyWiseTotal data={viewBidsBreakdownError ? [] : quoteData[0]} newData={[]} />
                        </table>
                    </div>
                    <div className="mt-4">
                        <div className="row">
                            <div className="col-6">
                                <label className="form-label">Remarks : {remarks}</label>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PriceBreakDownModal;
